import React, { useMemo } from 'react';
import { 
  Box, 
  Button, 
  CircularProgress, 
  Tooltip, 
  Typography,
  Badge,
  LinearProgress,
  useTheme
} from '@mui/material';
import { PlayCircle, StopCircle, Bot, MessageCircle, AlertCircle } from 'lucide-react';

const AutoNegotiationControl = ({
  isActive,
  isProcessing,
  onToggle,
  disabled,
  responseCount = 0,
  lastResponseTime = null,
  maxResponses = 5,
  error = null
}) => {
  const theme = useTheme();
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  const negotiationStatus = useMemo(() => {
    // Mover la función dentro del useMemo
    const getTimeSinceLastResponse = () => {
      if (!lastResponseTime) return '';
      const diff = new Date() - new Date(lastResponseTime);
      const minutes = Math.floor(diff / 60000);
      if (minutes < 1) return 'hace menos de un minuto';
      if (minutes === 1) return 'hace 1 minuto';
      if (minutes > 60) {
        const hours = Math.floor(minutes / 60);
        return `hace ${hours} ${hours === 1 ? 'hora' : 'horas'}`;
      }
      return `hace ${minutes} minutos`;
    };

    if (error) return {
      message: `Error: ${error}`,
      color: 'error.main',
      icon: <AlertCircle size={20} color="error" />
    };

    if (!isActive) return {
      message: 'Negociación automática desactivada',
      color: 'text.secondary',
      icon: null
    };

    if (isProcessing) return {
      message: 'Procesando respuesta...',
      color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
      icon: <CircularProgress size={16} />
    };

    if (responseCount === 0) return {
      message: 'Esperando mensajes...',
      color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
      icon: <Bot size={20} />
    };

    if (responseCount >= maxResponses) return {
      message: 'Límite de respuestas alcanzado',
      color: 'warning.main',
      icon: <AlertCircle size={20} color="warning" />
    };

    return {
      message: `Negociación activa - ${getTimeSinceLastResponse()}`,
      color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
      icon: <Bot size={20} />
    };
  }, [isActive, isProcessing, responseCount, error, maxResponses, lastResponseTime, isWallpaperMode]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        borderBottom: '1px solid',
        borderColor: isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : 'divider',
        backgroundColor: isActive
          ? (isWallpaperMode ? 'rgba(128, 128, 128, 0.2)' : 'rgba(3, 133, 255, 0.05)')
          : 'transparent',
        backdropFilter: isWallpaperMode && isActive ? 'blur(8px)' : 'none',
        WebkitBackdropFilter: isWallpaperMode && isActive ? 'blur(8px)' : 'none',
        transition: 'all 0.2s ease',
        opacity: disabled ? 0.5 : 1
      }}
    >
      <Tooltip title={negotiationStatus.message} placement="top">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 1.5,
            px: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant={isActive ? "contained" : "outlined"}
              size="medium"
              onClick={onToggle}
              disabled={disabled || isProcessing}
              startIcon={
                isProcessing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : isActive ? (
                  <StopCircle size={16} />
                ) : (
                  <PlayCircle size={16} />
                )
              }
              sx={{
                borderRadius: '20px',
                textTransform: 'none',
                minWidth: '220px',
                backgroundColor: isActive
                  ? (isWallpaperMode ? '#FFFFFF' : '#0385FF')
                  : 'transparent',
                borderColor: isActive
                  ? (isWallpaperMode ? '#FFFFFF' : '#0385FF')
                  : (isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : 'rgba(0,0,0,0.12)'),
                color: isActive
                  ? (isWallpaperMode ? '#000000' : '#fff')
                  : (isWallpaperMode ? '#FFFFFF' : 'inherit'),
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: isActive
                    ? (isWallpaperMode ? 'rgba(255, 255, 255, 0.85)' : '#0374e4')
                    : (isWallpaperMode ? 'rgba(128, 128, 128, 0.1)' : 'rgba(0,0,0,0.04)'),
                  boxShadow: 'none'
                },
                '&.Mui-disabled': {
                  backgroundColor: isActive
                    ? (isWallpaperMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(3, 133, 255, 0.3)')
                    : 'transparent',
                  opacity: 0.7
                }
              }}
            >
              {isActive ? 'Detener Negociación Auto' : 'Iniciar Negociación Auto'}
            </Button>

            {isActive && (
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  color: negotiationStatus.color
                }}
              >
                {negotiationStatus.icon}
                <Typography 
                  variant="caption"
                  sx={{ color: 'inherit' }}
                >
                  {negotiationStatus.message}
                </Typography>
              </Box>
            )}
          </Box>

          {isActive && responseCount > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Tooltip
                title={`${responseCount} de ${maxResponses} respuestas máximas`}
                placement="top"
              >
                <Badge
                  badgeContent={responseCount}
                  color={responseCount >= maxResponses ? "warning" : "primary"}
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: responseCount >= maxResponses
                        ? theme.palette.warning.main
                        : (isWallpaperMode ? '#FFFFFF' : '#0385FF'),
                      color: responseCount >= maxResponses
                        ? '#fff'
                        : (isWallpaperMode ? '#000000' : '#fff')
                    }
                  }}
                >
                  <MessageCircle size={20} color={isWallpaperMode ? '#FFFFFF' : undefined} />
                </Badge>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Tooltip>

      {isProcessing && (
        <LinearProgress
          sx={{
            height: 1,
            backgroundColor: isWallpaperMode
              ? 'rgba(128, 128, 128, 0.2)'
              : 'rgba(3, 133, 255, 0.1)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: isWallpaperMode ? '#FFFFFF' : '#0385FF'
            }
          }}
        />
      )}

      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: .5; }
          }
        `}
      </style>
    </Box>
  );
};

export default AutoNegotiationControl;